import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchAsyncTransactionDetails } from "redux/embrillProfileSlice";
import { useToasts } from "react-toast-notifications";
import { getFirstDayOfMonth, getLastDayOfMonth, formatDate } from "jsFunctions/dateTime";

const useTransactionData = (selectedPeriod, dateRange, type) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();

  const [loading, setLoading] = useState(false);
  const [allTransactions, setAllTransactions] = useState([]);
  const [dateRangeText, setDateRangeText] = useState('');

  const dateRanges = {
    lastOneWeek: () => {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 6);
      return { startDate, endDate: today };
    },
    last15Days: () => {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 14);
      return { startDate, endDate: today };
    },
    currentMonth: () => {
      const today = new Date();
      return { startDate: getFirstDayOfMonth(today), endDate: today };
    },
    lastMonth: () => {
      const today = new Date();
      const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return { startDate: getFirstDayOfMonth(prevMonthDate), endDate: getLastDayOfMonth(prevMonthDate) };
    },
    last3Months: () => {
      const today = new Date();
      const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      return { startDate: getFirstDayOfMonth(threeMonthsAgo), endDate: today };
    },
    last6Months: () => {
      const today = new Date();
      const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      return { startDate: getFirstDayOfMonth(sixMonthsAgo), endDate: today };
    },
    currentFinancialYear: () => {
      const today = new Date();
      return { startDate: new Date(today.getFullYear(), 3, 1), endDate: new Date(today.getFullYear() + 1, 2, 31) };
    },
    lastFinancialYear: () => {
      const today = new Date();
      return { startDate: new Date(today.getFullYear() - 1, 3, 1), endDate: new Date(today.getFullYear(), 2, 31) };
    },
    custom: () => ({ startDate: dateRange.startDate, endDate: dateRange.endDate }),
  };

  useEffect(() => {
    const { startDate, endDate } = dateRanges[selectedPeriod] ? dateRanges[selectedPeriod]() : { startDate: null, endDate: null };

    if (startDate && endDate) {
      setDateRangeText(`${formatDate(startDate)} to ${formatDate(endDate)}`);
      fetchTransactionDetails(startDate, endDate, type);
    } else {
      setDateRangeText('');
    }
  }, [selectedPeriod, dateRange, type]);

  const fetchTransactionDetails = async (startDate, endDate, type) => {
    setLoading(true);
    const data = { startDate, endDate, type };

    try {
      const res = await dispatch(fetchAsyncTransactionDetails(data));
      if (res?.payload) {
        setAllTransactions(res.payload);
      } else {
        addToast(`${res?.error?.message}`, { appearance: "error" });
      }
    } catch (error) {
      addToast(`Failed to fetch transaction details`, { appearance: "error" });
    } finally {
      setLoading(false);
    }
  };

  return { loading, allTransactions, dateRangeText };
};

export default useTransactionData;
